/**
 * Owner: Masa Bayu
 * File: AdminPublicationsScreen.tsx
 * Date:Time: 2026-10-23 10:20
 * Description: §467-D — seksyen Katalog: satu baris per terbitan dengan pemilik,
 *   harga, jualan hidup dan status artifak. Penapisan hanya mempersempit baris
 *   yang DIHANTAR hub (tiada carian ke pelayan, tiada row rekaan). Harga dibaca
 *   sahaja: ruling §467d-R — pentadbir memantau, harga ditulis penerbit semasa
 *   Publish.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView } from './admin-dashboard';
import { NO_DATA, bookRow, type AdminBookRow } from './admin-view';
import { Icon } from './AdminIcons';

const HEAD = 25;

function matches(book: AdminBookRow, needle: string): boolean {
  if (needle.length === 0) return true;
  return (
    book.title.toLowerCase().includes(needle) ||
    book.publicationId.toLowerCase().includes(needle) ||
    book.ownerText.toLowerCase().includes(needle) ||
    book.categoryText.toLowerCase().includes(needle)
  );
}

function Badge({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'neutral';
  children: string;
}): ReactElement {
  return <span className={`qxa__pill qxa__pill--${tone}`}>{children}</span>;
}

export interface AdminPublicationsScreenProps {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
}

export function AdminPublicationsScreen({ view, la }: AdminPublicationsScreenProps): ReactElement {
  const [needle, setNeedle] = useState('');
  const [expanded, setExpanded] = useState(false);

  const all = useMemo<readonly AdminBookRow[]>(
    () => view.publications.map((book) => bookRow(book, la)),
    [view.publications, la],
  );
  const rows = useMemo<readonly AdminBookRow[]>(() => {
    const lower = needle.trim().toLowerCase();
    return all.filter((book) => matches(book, lower));
  }, [all, needle]);

  const shown = expanded ? rows : rows.slice(0, HEAD);
  const priced = view.catalog.pricedPublications;

  return (
    <div className="qxa__stack">
      <section className="qxa__filters" aria-label={la.searchLabel}>
        <label className="qxa__field">
          <span className="qxa__field-label">{la.searchLabel}</span>
          <input
            type="search"
            value={needle}
            placeholder={la.searchPlaceholder}
            onChange={(event) => {
              setNeedle(event.target.value);
              setExpanded(false);
            }}
          />
        </label>
        <p className="qxa__match">
          {String(rows.length)} {la.matchCount} · {String(priced)}/
          {String(view.catalog.publications)} {la.kpiPriced.toLowerCase()}
        </p>
      </section>

      <section className="qxa__card" aria-label={la.publicationsTitle}>
        {rows.length === 0 ? (
          <p className="qxa__empty">{la.publicationsEmpty}</p>
        ) : (
          <>
            <div className="qxa__table-wrap">
              <table className="qxa__table">
                <caption className="qxa__sr">{la.publicationsTitle}</caption>
                <thead>
                  <tr>
                    <th scope="col">{la.colTitle}</th>
                    <th scope="col">{la.colOwner}</th>
                    <th scope="col">{la.colRevision}</th>
                    <th scope="col">{la.colPrice}</th>
                    <th scope="col">{la.colCategory}</th>
                    <th scope="col">{la.colSales}</th>
                    <th scope="col">{la.colRevenue}</th>
                    <th scope="col">{la.colArtifact}</th>
                    <th scope="col">{la.colPublished}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr key={row.publicationId}>
                      <th scope="row" data-label={la.colTitle}>
                        <span className="qxa__cell-title">{row.title}</span>
                        <code className="qxa__row-id">{row.publicationId}</code>
                      </th>
                      <td data-label={la.colOwner}>
                        {row.ownerAccountId === null ? (
                          <Badge tone="warn">{la.unownedBadge}</Badge>
                        ) : (
                          row.ownerText
                        )}
                      </td>
                      <td data-label={la.colRevision}>
                        {row.revision === null ? NO_DATA : String(row.revision)}
                      </td>
                      <td data-label={la.colPrice}>
                        {row.priceMinorUnits === null ? (
                          <Badge tone="neutral">{la.unpricedBadge}</Badge>
                        ) : (
                          row.priceText
                        )}
                      </td>
                      <td data-label={la.colCategory}>{row.categoryText}</td>
                      <td data-label={la.colSales}>
                        {String(row.sales.orders)} · {String(row.sales.buyers)}{' '}
                        {la.colBuyers.toLowerCase()}
                      </td>
                      <td data-label={la.colRevenue}>{row.revenueText}</td>
                      <td data-label={la.colArtifact}>
                        <span
                          className={`qxa__flag qxa__flag--${row.artifactPresent ? 'ok' : 'bad'}`}
                        >
                          <Icon name={row.artifactPresent ? 'check' : 'alert'} size={15} />
                          <span>
                            {row.artifactPresent ? la.artifactPresent : la.artifactMissing}
                          </span>
                        </span>
                      </td>
                      <td data-label={la.colPublished}>{row.publishedText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rows.length > HEAD ? (
              <button
                type="button"
                className="qxa__btn qxa__btn--ghost qxa__more"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? la.showFewer : `${la.showAll} (${String(rows.length - HEAD)})`}
              </button>
            ) : null}
          </>
        )}
        <p className="qxa__note">{la.priceNote}</p>
      </section>
    </div>
  );
}
